/**
 * @fileoverview Reconciles a "selected row" object with a liveQuery-backed list.
 * @module ui-admin/hooks/useLiveSelection
 *
 * Responsibilities: - Keep an open slide-in panel's selected object in sync with
 *                     the live list: replace it when a fresher row arrives, and
 *                     close the panel when the row is deleted.
 *                   - Avoid the two stale-snapshot races: (a) a mutation handler
 *                     sets a NEWER selected object before the list refresh lands —
 *                     the older list row must not clobber it; (b) a newly created
 *                     record is selected before it appears in the list — the panel
 *                     must not close just because an older emission lacks it.
 * Dependencies:     - React hooks only; no Dexie, no API.
 * Architecture:     - Freshness is decided by `updatedAt` when both objects carry
 *                     it (ISO strings compare lexically); when either side lacks a
 *                     timestamp the live row is treated as authoritative.
 *                   - Deletion is detected only when the selected key WAS present
 *                     in a previous list emission and is now absent — an id never
 *                     seen in the list is treated as "pending creation", not a
 *                     delete.
 * Notes:            - All GridPageShell pages that hold a selected object (not a
 *                     selected id) should use this instead of hand-rolled effects.
 */

import { useEffect, useRef } from 'react';

/**
 * @contract useLiveSelection
 * Generic:   T — the row type; must expose the key via `keyOf` (default `_id`)
 *            and may expose `updatedAt` (ISO string) for freshness ordering.
 * Purpose:   Reconcile `selected` with `list` on every list emission.
 * Inputs:    list      — the liveQuery-backed row array.
 *            selected  — the currently selected row object (or null).
 *            setSelected — state setter for the selected row.
 *            keyOf     — row key extractor (defaults to `row._id`).
 * Behavior:  - Replaces `selected` when a live row with the same key exists and
 *            is not older than `selected`.
 *            - Sets `selected` to null only when the key was present in a prior
 *            emission and has since disappeared (a real delete).
 */
export function useLiveSelection<T>(
  list: readonly T[],
  selected: T | null,
  setSelected: (next: T | null) => void,
  keyOf: (item: T) => string | undefined = (item) => (item as { _id?: string })._id,
): void {
  const seenIdsRef = useRef<Set<string | undefined>>(new Set());
  const keyOfRef = useRef(keyOf);
  keyOfRef.current = keyOf;

  useEffect(() => {
    const ids = new Set(list.map((r) => keyOfRef.current(r)));
    if (selected) {
      const key = keyOfRef.current(selected);
      const live = list.find((r) => keyOfRef.current(r) === key);
      if (live && live !== selected) {
        const selAt = (selected as { updatedAt?: string }).updatedAt;
        const liveAt = (live as { updatedAt?: string }).updatedAt;
        if (!selAt || !liveAt || liveAt >= selAt) setSelected(live);
      } else if (!live && seenIdsRef.current.has(key)) {
        // Existed in a previous emission and is now gone — a real delete.
        setSelected(null);
      }
    }
    seenIdsRef.current = ids;
  }, [list, selected, setSelected]);
}
